import Hls, { ErrorTypes, Events, type HlsConfig } from "hls.js";
import { PlayerEventEmitter } from "./events.js";
import { PlayerFailure, sanitizeUrl, toPlayerError } from "./errors.js";
import { installMseAudioCodecFallback } from "./mse-codec-fallback.js";
import type {
  AspectMode,
  MediaTrack,
  Player,
  PlayerErrorCode,
  PlayerEvent,
  PlayerEventPayload,
  PlayerMode,
  TimeshiftWindow,
  PlayerSource,
  PlayerState,
} from "./interface.js";
import { ZOOM_FACTOR } from "./interface.js";

/**
 * Minimal structural view of the mpegts.js module and the player objects it
 * creates. The real module is loaded lazily in the browser only; tests inject
 * a fake factory, so importing this package never touches mpegts.js under Node.
 */
export interface MpegtsPlayerLike {
  attachMediaElement(mediaElement: HTMLMediaElement): void;
  load(): void;
  unload(): void;
  destroy(): void;
  on(event: string, listener: (...args: unknown[]) => void): void;
}

export interface MpegtsFactory {
  createPlayer(
    source: { type: string; isLive?: boolean; url?: string },
    config?: Record<string, unknown>,
  ): MpegtsPlayerLike;
  Events: { ERROR: string };
  ErrorTypes: { NETWORK_ERROR: string; MEDIA_ERROR: string; OTHER_ERROR: string };
  ErrorDetails: {
    NETWORK_EXCEPTION: string;
    NETWORK_STATUS_CODE_INVALID: string;
    NETWORK_TIMEOUT: string;
    MEDIA_FORMAT_ERROR: string;
    MEDIA_CODEC_UNSUPPORTED: string;
    MEDIA_MSE_ERROR: string;
  };
}

export interface HlsPlayerOptions {
  video: HTMLVideoElement;
  hlsConfig?: Partial<HlsConfig>;
  hlsFactory?: (config: Partial<HlsConfig>) => Hls;
  isHlsSupported?: () => boolean;
  /** MPEG-TS engine. Defaults to the real mpegts.js module, loaded lazily in the browser. */
  mpegtsFactory?: MpegtsFactory;
  isMpegtsSupported?: () => boolean;
  mpegtsConfig?: Record<string, unknown>;
  /** Same-URL retry backoff for network errors; defaults to 1s→5s cap. Tests pass () => 0. */
  networkRetryBackoffMs?: (retryCount: number) => number;
  /** Live auto-reconnect count after all URLs fail; defaults to 3. */
  maxLiveReconnects?: number;
  /** Pause before a live reconnect attempt; defaults to 2000ms. Tests pass 0. */
  liveReconnectDelayMs?: number;
  /**
   * Live stall watchdog: if playback is stuck in `waiting` for this many ms
   * with no forward progress, the source auto-reloads. Default 8000ms. Tests
   * pass a small value.
   */
  stallTimeoutMs?: number;
  /** Auto-reloads per stall before escalating to live reconnect / error. Default 3. */
  maxStallRecovers?: number;
  /**
   * How long a single source may take to produce its FIRST frame before the
   * player treats it as dead and runs the normal recovery chain. Default 45s.
   * Tests pass a small value.
   */
  firstFrameTimeoutMs?: number;
  /**
   * The whole start's shared first-frame budget: the first URL, every mirror
   * and every live reconnect together may not wait longer than this. Default
   * 120s. Tests pass a small value.
   */
  firstFrameBudgetMs?: number;
  /**
   * Debounce for transcoded far-seek restarts (see seek()). Default 400ms so a
   * drag across the seek bar collapses to one restart. Tests pass 0.
   */
  transcodedSeekDebounceMs?: number;
}

// Flaky providers drop connections and burn mpegts.js's internal loader
// retries before erroring. Give the SAME URL a few fresh attempts with
// ExoPlayer-style exponential-ish backoff (1s, 2s, 3s, 4s, capped at 5s)
// before advancing to alt URLs / live reconnect / exhaustion. Codec/media
// errors skip the network window and fail fast because retrying cannot help.
const MAX_MPEGTS_NETWORK_RETRIES = 4;
// ExoPlayer's DefaultLoadErrorHandlingPolicy uses min((count-1)*1000, 5000);
// we start at 1s so the first retry isn't instant, matching real IPTV apps.
const defaultNetworkRetryBackoffMs = (retryCount: number): number => Math.min(retryCount * 1000, 5000);
// How many times a LIVE source reloads from the top after every URL fails.
// Bounded so a permanently dead channel still surfaces an error, while a
// flaky panel (407/502 bursts, brief outages) recovers like SparkleTV does.
const MAX_LIVE_RECONNECTS = 3;
const LIVE_RECONNECT_DELAY_MS = 2000;
// If a live source stalls in `waiting` (provider blip, dead segment window)
// for this long, reload the source from scratch — the way ExoPlayer recovers
// from `playbackStalledUnderrun`. 8s keeps a blip invisible to the user while
// still surfacing a genuinely dead channel within ~half a minute.
const STALL_TIMEOUT_MS = 8000;
const MAX_STALL_RECOVERS = 3;
// No first frame within this long means the stream is not coming.
//
// The stall watchdog above deliberately does NOT cover the initial load — it
// only arms for a source that has already played (see onWaiting) — and the
// engine's own fetcher timeouts only fire when a request stops answering. A
// stream that keeps trickling data it never turns into a frame therefore had
// nothing watching it at all: the guide's preview sat on "Loading…" for as long
// as the viewer was willing to look at it, which reads as a dead channel
// (owner, 2026-09-23).
//
// 45s is ~3x the worst startup measured on this project (live HLS 10.7s, an MKV
// transcode 14.2s — docs/stream-telemetry.md), so it only fires on a stream that
// genuinely is not coming; a slow-but-working channel is never cut off.
const FIRST_FRAME_TIMEOUT_MS = 45_000;
// Subsequent attempts of the SAME load get this instead. The first attempt
// covers the provider handshake, which is genuinely slow (10-14s measured); a
// mirror or a reconnect of a URL that already failed to start is not entitled to
// another 45s, or a dead channel would take four of them to say so.
const FIRST_FRAME_RETRY_GRACE_MS = 15_000;
// The BUDGET for one start, shared by every attempt above: the first URL, each
// mirror, each live reconnect. Without it the per-attempt deadlines stack — 45s,
// then 15s per mirror and per reconnect round — and a live channel with mirrors
// can sit on "Retrying…" for minutes, which is the very complaint the deadline
// exists to answer (owner, 2026-09-24). Two minutes is comfortably above every
// real start measured here (10.7s live, 14.2s transcode), leaves room for a
// mirror or two, and stays short of the point where a viewer gives up on the app
// rather than on the channel.
const FIRST_FRAME_BUDGET_MS = 120_000;
// After a watchdog reload succeeds, playback may blip again within seconds.
// Don't reset the stall-recovery budget until the stream has played cleanly
// for this long, so rapid-fire blips escalate instead of reloading forever.
const STALL_RESET_GRACE_MS = 15000;

// Deep-buffer live tuning for HLS, mirroring the mpegts isLive:false fix.
// A higher maxBufferLength lets hls.js ride through provider blips the way the
// mpegts path does.
//
// liveSyncDurationCount MUST stay below the number of fragments the provider
// actually publishes. This was raised to 6 to buy rewind headroom, and it
// stopped live TV from starting at all: the provider publishes exactly 6
// fragments (11s each, a 66s window), so hls.js targeted the OLDEST fragment,
// which rolls off before it can be fetched. It never settled on a start
// position, so it reloaded the playlist forever and never requested a single
// segment — no error, because nothing failed. Measured 2026-08-28: at 6,
// readyState stayed 0 with an empty buffer and zero segment requests; at 3, the
// same channel reached readyState 4 and played. Do not raise this without
// checking how many fragments the playlist actually carries.
//
// liveMaxLatencyDurationCount caps how far behind the edge we can drift before
// hls.js forces a catch-up to live. NOTE: 10 fragments is ~110s against that
// same 66s window, so it is larger than the provider retains — suspect for the
// same reason, but left alone so the fix above could be tested in isolation.
/**
 * Timeshift window: seconds of already-played live content kept behind the
 * playhead so the viewer can pause and rewind (Section 15 step 1).
 *
 * Chosen small ON PURPOSE. This is not a disk cache — hls.js retains it in the
 * MediaSource SourceBuffer, which is browser MEMORY, and browsers cap a
 * SourceBuffer at low hundreds of MB before evicting or throwing
 * QuotaExceededError. Live TV at ~5 Mbps is roughly 37 MB per minute, so even
 * this modest window is near what a constrained device will actually hold, and
 * a "30 minute rewind" would be a promise the browser silently breaks by
 * evicting the oldest data anyway.
 *
 * `Player.timeshift()` reports what is ACTUALLY retained rather than this
 * number, because the browser is the one deciding.
 *
 * A deep, reliable rewind is native-engine territory, where the buffer can live
 * on disk instead of in the decoder's memory.
 */
export const TIMESHIFT_WINDOW_SECONDS = 300;

/**
 * A refusal retrying cannot change: the server or provider said no (4xx, other
 * than a timeout or "too many requests") or the server's converter could not
 * start the stream (502). hls.js retried these four times with growing waits,
 * so a film the server refused in under a second showed its error only after
 * about 25 s (website, 2026-10-07). A live channel still recovers: its own
 * reconnect reloads it from the top after a pause (tryNextSource).
 */
export function definiteRefusal(status: number | undefined): boolean {
  if (status === undefined) return false;
  return (status >= 400 && status < 500 && status !== 408 && status !== 429) || status === 502;
}

/** The first playlist's retries: the old settings (4, from 1 s, doubling), but never for a definite refusal. */
const manifestRetry = {
  maxNumRetry: 4,
  retryDelayMs: 1000,
  maxRetryDelayMs: 64_000,
  shouldRetry: (_config: unknown, _count: number, _isTimeout: boolean, response: { code?: number } | undefined, retry: boolean) =>
    retry && !definiteRefusal(response?.code),
};

const defaultHlsConfig: Partial<HlsConfig> = {
  maxBufferLength: 60,
  // Retain played content behind the playhead so rewind has something to seek
  // into. Without this hls.js evicts it almost immediately and live is
  // effectively unrewindable.
  backBufferLength: TIMESHIFT_WINDOW_SECONDS,
  liveSyncDurationCount: 3,
  liveMaxLatencyDurationCount: 10,
  manifestLoadPolicy: {
    default: {
      maxTimeToFirstByteMs: 10_000,
      maxLoadTimeMs: 10_000,
      timeoutRetry: { maxNumRetry: 4, retryDelayMs: 1000, maxRetryDelayMs: 64_000 },
      errorRetry: manifestRetry,
    },
  },
  levelLoadingMaxRetry: 4,
  fragLoadingMaxRetry: 6,
  fragLoadingRetryDelay: 1000,
  fragLoadingMaxRetryTimeout: 8000,
};

// Live IPTV tuning: buffer-friendly. The server-side proxy provides a stable
// continuous feed with its own rolling buffer and transparent reconnect, so
// the browser player can treat every stream as non-live (isLive: false).
// This lets mpegts.js build a deep rolling buffer (target ~7s) instead of
// constantly trimming to a "live edge" that doesn't help us. Playback still
// starts as soon as the first frames decode (~1s, cable-like). The stash
// and sync knobs are all pushed toward buffering:
//
//   isLive: false            → no live-edge trimming; buffer stays deep
//   enableStashBuffer: true   → received-data stash smooths out jitter
//   stashInitialSize: 512 KB  → ~0.5s initial fill; buffer deepens after playback
//   liveBufferLatencyChasing: false → don't hunt the live edge
//   liveSync: false           → no time-remapping
//
// `enableWorker` stays false on purpose: some embedded webviews (and this
// app's Preview sandbox) block Web Workers, which crashed mpegts.js
// mid-init. Main-thread transmuxing of a single stream is fine.
const defaultMpegtsConfig: Record<string, unknown> = {
  enableWorker: false,
  // The proxy preserves direct VOD as a finite HTTP response (content-length
  // + range support). mpegts.js's VOD loader must be allowed to issue range
  // requests and finish naturally; forcing a large stash makes a slow VOD
  // oscillate between playback and loader pauses on older devices.
  isLive: false,
  enableStashBuffer: true,
  stashInitialSize: 512 * 1024,
  liveBufferLatencyChasing: false,
  liveSync: false,
};

// Transcoded VOD is a DIFFERENT shape from direct VOD: the proxy re-muxes
// mkv/avi through FFmpeg into a live-shaped MPEG-TS pipe — no content-length,
// no range support, ends only when the source file does. When the total
// duration is known (TMDB runtime), the finite VOD loader is used with a
// filesize estimate so EOF is handled gracefully — the buffer fills in chunks
// from the download, yielding smooth playback without the every-minute abort
// storm. When duration is unknown, mpegts.js must treat the pipe as live
// (isLive: true) to avoid the finite loader suspending/reconnecting on EOF.
// All the deep-buffer knobs below carry over in both modes.
const transcodedMpegtsConfig: Record<string, unknown> = {
  ...defaultMpegtsConfig,
  isLive: true,
  // The startup cushion is NOT set here. A byte-sized stash is the wrong unit: a
  // fixed 4 MB is ~6s of a 5.3 Mbps remux but ~16s of a 2 Mbps one, so the wait
  // scales backwards with bitrate. The cushion is enforced in seconds instead —
  // see TRANSCODED_PREBUFFER_SECONDS and waitForPrebuffer().
};

/**
 * Seconds of content to accumulate before a transcoded VOD starts playing.
 *
 * `180 - 2026` played at 0.21x realtime with ~5 fps because playback began with
 * under a second buffered. On a link with modest headroom it could then never
 * build a cushion while draining it — yet it reached a full 24 fps at 1.00x with
 * zero dropped frames the moment something paused it long enough to get ahead.
 * The ffmpeg pipe (15x realtime), the network and the decoder were all fine.
 *
 * Expressed in seconds so the wait is proportional to the link rather than the
 * bitrate: filling N seconds costs N x (bitrate / bandwidth), so a 3s cushion is
 * roughly a 2s wait at the measured 5.3 Mbps over 8.2 Mbps, and stays about that
 * on a lower-bitrate title instead of ballooning.
 */
const TRANSCODED_PREBUFFER_SECONDS = 8;

/** Never hold playback longer than this waiting for the cushion. */
// Raised from 8s: at startup the pipe fills slowly enough that the old timeout
// expired before the cushion was reached, so playback began with ~0.4s buffered
// and stalled within seconds. Measured 2026-08-26. The wait is bounded, and a
// stall that follows now recovers by buffering rather than restarting, so
// erring toward a longer wait is strictly better than starting starved.
const TRANSCODED_PREBUFFER_TIMEOUT_MS = 25000;

// Debounce for far-seek transcode restarts. The seek bar fires `seek()` on
// every drag step; without the debounce, dragging across a movie would kill
// and re-spawn the transcode session for EVERY intermediate position. The
// latest out-of-window target is applied when the drag settles (400ms of no
// new seeks), collapsing a full drag into one restart at the release point.
const TRANSCODED_SEEK_DEBOUNCE_MS = 400;

let realMpegts: MpegtsFactory | undefined;

async function getMpegtsFactory(): Promise<MpegtsFactory> {
  if (!realMpegts) {
    // Dynamic import keeps Node test environments working: mpegts.js is a
    // browser bundle that references `self` at module scope and crashes under
    // Node. The type-only import above is erased at runtime.
    realMpegts = ((await import("mpegts.js")).default as unknown) as MpegtsFactory;
  }
  return realMpegts;
}

/**
 * Pick a playback engine from a provider URL. Extensionless URLs are common in
 * IPTV (tokenized `/live/play/<token>/<id>` stream servers), and raw MPEG-TS
 * is the default live transport for Xtream/XUI panels, so the fallback is TS.
 * Callers that know better (e.g. after a server-side probe) can pass an
 * explicit `PlayerSource.format` instead.
 */
export function detectPlaybackMode(url: string): PlayerMode {
  let pathname: string;
  try {
    pathname = new URL(url, "http://player.local").pathname.toLowerCase();
  } catch {
    return "mpegts";
  }
  if (pathname.endsWith(".m3u8")) return "hls";
  // mkv/avi carry AC3/DTS/EAC3 audio that Chromium cannot decode (video plays
  // silently). The stream proxy re-muxes them to MPEG-TS (video copied, audio
  // → AAC), so play them through mpegts.js — the same engine used for live
  // MPEG-TS channels. Native playback would drop the audio track entirely.
  if (/\.(mkv|avi)$/.test(pathname)) return "mpegts";
  // Container formats the browser's own decoder handles natively.
  if (/\.(mp4|webm|ogv|ogg|mov|m4v)$/.test(pathname)) return "native";
  // Extensionless/tokenized URLs are Xtream live streams — raw MPEG-TS.
  return "mpegts";
}

export class HlsPlayer implements Player {
  private readonly video: HTMLVideoElement;
  private readonly emitter = new PlayerEventEmitter();
  private readonly config: Partial<HlsConfig>;
  private readonly hlsFactory: (config: Partial<HlsConfig>) => Hls;
  private readonly isHlsSupported: () => boolean;
  private readonly mpegtsFactory?: MpegtsFactory;
  private readonly isMpegtsSupported: () => boolean;
  private readonly mpegtsConfig?: Record<string, unknown>;
  private hls: Hls | null = null;
  private mpegts: MpegtsPlayerLike | null = null;
  private source: PlayerSource | undefined;
  private sources: string[] = [];
  private sourceIndex = 0;
  private destroyed = false;
  private loadGeneration = 0;
  private retryInFlight = false;
  private mpegtsNetworkRetries = 0;
  private liveReconnects = 0;
  private readonly networkRetryBackoffMs: (retryCount: number) => number;
  private readonly maxLiveReconnects: number;
  private readonly liveReconnectDelayMs: number;
  private readonly stallTimeoutMs: number;
  private readonly maxStallRecovers: number;
  private readonly firstFrameTimeoutMs: number;
  private readonly firstFrameBudgetMs: number;
  private stallTimer: ReturnType<typeof setTimeout> | null = null;
  private firstFrameTimer: ReturnType<typeof setTimeout> | null = null;
  /** Attempts armed for the current load: the first gets the full deadline. */
  private firstFrameAttempts = 0;
  /** When this start's shared budget runs out; no budget until a load begins. */
  private firstFrameBudgetEndsAt = Number.POSITIVE_INFINITY;
  private stallRecovers = 0;
  private lastRecoveryAt = 0;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private nativeSourceUrl: string | undefined;
  /** A seek requested before metadata loaded (video.currentTime is a no-op
   * until then); applied on loadedmetadata. Cleared by load(). */
  private pendingSeek: number | null = null;
  /** The last seek the user requested (persists across stall-recovery reloads
   * so the position survives a source reset). Cleared by load(). */
  private lastSeekTarget: number | null = null;
  /**
   * The transcoded VOD sliding window as reported by hls.js level details:
   * `start` = oldest retained segment (seek floor), `end` = transcode frontier
   * (seek ceiling). Updated on every playlist refresh. Null for non-HLS and
   * before the first manifest load. These are SESSION-RELATIVE (0-based) —
   * movie time = element time + transcodedOffset.
   */
  private seekBounds: { start: number; end: number } | null = null;
  /**
   * Movie-time second the current transcode session started at. 0 for the
   * initial session; a far-seek restart re-creates the session at the new
   * offset with 0-based segments again, so every value crossing the player
   * boundary (state.currentTime, seek targets, the seek bar, saved progress)
   * is movie time while the video element stays session-relative.
   */
  private transcodedOffset = 0;
  /** Pending far-seek target + timer; see debounceTranscodedSeek(). */
  private transcodedSeekTarget: number | null = null;
  private transcodedSeekTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly transcodedSeekDebounceMs: number;
  private state: PlayerState = {
    status: "idle",
    currentTime: 0,
    duration: 0,
    bufferedAhead: 0,
    volume: 1,
    muted: false,
    attempt: 0,
  };

  constructor(options: HlsPlayerOptions) {
    this.video = options.video;
    this.config = { ...defaultHlsConfig, ...options.hlsConfig };
    this.hlsFactory = options.hlsFactory ?? ((config) => new Hls(config));
    this.isHlsSupported = options.isHlsSupported ?? (() => Hls.isSupported());
    this.mpegtsFactory = options.mpegtsFactory;
    this.isMpegtsSupported = options.isMpegtsSupported ?? (() => {
      if (typeof window === "undefined") return false;
      // Safari 17+ uses ManagedMediaSource; mpegts.js supports both.
      const win = window as Window & { ManagedMediaSource?: unknown };
      return typeof MediaSource !== "undefined" || typeof win.ManagedMediaSource !== "undefined";
    });
    this.mpegtsConfig = { ...defaultMpegtsConfig, ...options.mpegtsConfig };
    this.networkRetryBackoffMs = options.networkRetryBackoffMs ?? defaultNetworkRetryBackoffMs;
    this.maxLiveReconnects = options.maxLiveReconnects ?? MAX_LIVE_RECONNECTS;
    this.liveReconnectDelayMs = options.liveReconnectDelayMs ?? LIVE_RECONNECT_DELAY_MS;
    this.stallTimeoutMs = options.stallTimeoutMs ?? STALL_TIMEOUT_MS;
    this.maxStallRecovers = options.maxStallRecovers ?? MAX_STALL_RECOVERS;
    this.firstFrameTimeoutMs = options.firstFrameTimeoutMs ?? FIRST_FRAME_TIMEOUT_MS;
    this.firstFrameBudgetMs = options.firstFrameBudgetMs ?? FIRST_FRAME_BUDGET_MS;
    this.transcodedSeekDebounceMs = options.transcodedSeekDebounceMs ?? TRANSCODED_SEEK_DEBOUNCE_MS;
    this.video.addEventListener("playing", this.onPlaying);
    this.video.addEventListener("pause", this.onPause);
    this.video.addEventListener("waiting", this.onWaiting);
    this.video.addEventListener("ended", this.onEnded);
    this.video.addEventListener("timeupdate", this.onTimeUpdate);
    this.video.addEventListener("loadedmetadata", this.onLoadedMetadata);
    this.video.addEventListener("error", this.onVideoError);
  }

  async load(source: PlayerSource): Promise<void> {
    this.ensureAlive();
    if (!source.url) throw new PlayerFailure("manifest", "A stream URL is required.", false);
    this.loadGeneration += 1;
    this.retryInFlight = false;
    this.mpegtsNetworkRetries = 0;
    this.liveReconnects = 0;
    this.stallRecovers = 0;
    // A new source gets the full first-frame deadline, not the retry grace, and
    // its own budget — the previous channel's spent budget must not end this one.
    this.firstFrameAttempts = 0;
    this.beginFirstFrameBudget();
    this.pendingSeek = null;
    this.lastSeekTarget = null;
    this.seekBounds = null;
    this.transcodedOffset = 0;
    this.transcodedSeekTarget = null;
    this.clearTimers();
    this.source = { ...source, url: sanitizeUrl(source.url) ?? "[redacted stream URL]", altUrls: source.altUrls?.map((url) => sanitizeUrl(url) ?? "[redacted stream URL]") };
    const playbackUrls = [source.url, ...(source.altUrls ?? [])].filter(Boolean);
    this.sources = playbackUrls;
    this.sourceIndex = 0;
    this.nativeSourceUrl = undefined;
    this.setState({ status: "loading", source: this.source, attempt: 1, error: undefined });
    try {
      await this.loadCurrentSource();
    } catch (error) {
      this.fail(error);
      throw error;
    }
  }

  /**
   * Hold a transcoded VOD until it has a few seconds buffered. mpegts.js keeps
   * filling the buffer while the element is paused, so this costs a short wait
   * once instead of stuttering for the whole title. Resolves early on timeout so
   * a genuinely slow source still starts rather than hanging.
   */
  private async waitForPrebuffer(): Promise<void> {
    if (this.source?.transcoded !== true) return;
    const deadline = Date.now() + TRANSCODED_PREBUFFER_TIMEOUT_MS;
    while (Date.now() < deadline) {
      if (this.destroyed) return;
      const { buffered, currentTime } = this.video;
      let ahead = 0;
      for (let i = 0; i < buffered.length; i += 1) {
        if (currentTime >= buffered.start(i) && currentTime <= buffered.end(i)) {
          ahead = buffered.end(i) - currentTime;
          break;
        }
      }
      if (ahead >= TRANSCODED_PREBUFFER_SECONDS) return;
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }

  async play(): Promise<void> {
    this.ensureAlive();
    try {
      await this.waitForPrebuffer();
      this.ensureAlive();
      await this.video.play();
    } catch (error) {
      // Autoplay policy: if the user gesture expired during a slow stream
      // load (e.g. the provider was slow to answer), video.play() rejects
      // with NotAllowedError even though the user DID click. Muted autoplay
      // is always permitted, so retry muted and restore the unmuted state
      // once playback actually starts.
      const isAutoplayBlock = error instanceof DOMException && error.name === "NotAllowedError";
      if (isAutoplayBlock && !this.video.muted) {
        this.video.muted = true;
        try {
          await this.video.play();
          this.video.muted = false;
          this.setState({ muted: false, volume: this.video.volume });
          return;
        } catch {
          this.video.muted = false;
        }
      }
      // If there is no media at all (readyState 0-1: manifest/segment load
      // failed), this is NOT an autoplay-policy problem — surfacing the
      // click-muted retry here just produced a misleading "Playback needs a
      // tap or click" while the real failure was upstream. Name the actual
      // cause so the retry/failure path does the right thing.
      const nothingLoaded = this.video.readyState < 2;
      const playerError = new PlayerFailure(
        nothingLoaded ? "network" : "autoplay",
        nothingLoaded
          ? "The stream could not be loaded."
          : "Playback needs a tap or click before audio can start.",
        true,
        this.source?.url,
      );
      this.fail(playerError);
      throw playerError;
    }
  }

  pause(): void {
    this.ensureAlive();
    this.video.pause();
  }

  /**
   * Convert a movie-time seek target to the element's session-relative
   * timeline (identity for non-transcoded sources).
   */
  private toElementTime(movieSeconds: number): number {
    return this.source?.transcoded === true ? movieSeconds - this.transcodedOffset : movieSeconds;
  }

  /** Convert the element's session-relative currentTime to movie time. */
  private toMovieTime(elementSeconds: number): number {
    return this.source?.transcoded === true ? elementSeconds + this.transcodedOffset : elementSeconds;
  }

  /**
   * Clamp a seek to what actually exists on the server, in MOVIE time.
   *
   * Transcoded VOD plays from a SLIDING window on disk — the transcode races
   * ahead but is throttled to stay ~12 min ahead of the playhead, and only the
   * newest ~45 min of segments are retained. A seek below the window start
   * asks for a deleted segment (404-fatal for hls.js), and one beyond the
   * frontier asks for a segment ffmpeg has not written yet. Both edges are
   * read live from hls.js level details (session-relative, so the session
   * offset is added back), meaning a drag anywhere on the bar lands on a
   * segment that exists — no 404 loops, no restart-from-zero.
   *
   * Non-transcoded streams keep the historical behavior: low = 0, high =
   * `video.duration` (Infinity for live, which hls.js clamps internally).
   */
  private clampSeek(target: number): number {
    let low = 0;
    let high = Number.isFinite(this.video.duration) && this.video.duration > 0 ? this.video.duration : Infinity;
    if (this.source?.transcoded === true && this.seekBounds) {
      low = this.seekBounds.start + this.transcodedOffset;
      high = this.seekBounds.end + this.transcodedOffset;
    }
    return Math.min(Math.max(target, low), high);
  }

  /**
   * What is retained behind the playhead right now.
   *
   * Reads the element's own buffered ranges rather than the configured window:
   * the back buffer lives in the SourceBuffer and the browser evicts from it
   * under memory pressure, so the configured 5 minutes may in practice be two.
   * A UI built on the configured number would offer rewinds that fail.
   */
  timeshift(): TimeshiftWindow {
    const unavailable: TimeshiftWindow = { available: false, behindSeconds: 0, offsetSeconds: 0 };
    if (this.source?.type !== "live") return unavailable;
    const video = this.video;
    const buffered = video.buffered;
    if (!buffered || buffered.length === 0) return unavailable;
    const current = video.currentTime;
    // The range containing the playhead is the only one that can be rewound
    // into; a gap means the earlier data is gone.
    let start = current;
    let end = current;
    for (let i = 0; i < buffered.length; i += 1) {
      if (current >= buffered.start(i) - 0.5 && current <= buffered.end(i) + 0.5) {
        start = buffered.start(i);
        end = buffered.end(i);
        break;
      }
    }
    const behindSeconds = Math.max(0, current - start);
    // The live edge is the furthest buffered point, which is where hls.js is
    // appending. seekable.end() would be more precise on some streams but is
    // unset on several provider feeds, and this never lies about being ahead.
    const offsetSeconds = Math.max(0, end - current);
    return {
      available: behindSeconds > 1,
      behindSeconds,
      offsetSeconds,
    };
  }

  seekFromLiveEdge(offsetSeconds: number): void {
    this.ensureAlive();
    if (this.source?.type !== "live") return;
    const window = this.timeshift();
    if (!window.available && offsetSeconds < 0) return;
    const video = this.video;
    const edge = video.currentTime + window.offsetSeconds;
    // Clamp into what is actually retained: asking for more than the browser
    // kept would seek into a hole and stall.
    const wanted = Math.min(0, offsetSeconds);
    const limited = Math.max(wanted, -window.behindSeconds);
    video.currentTime = Math.max(0, edge + limited);
  }

  seek(seconds: number): void {
    this.ensureAlive();
    const target = Math.max(0, seconds);
    // Transcoded VOD: a seek OUTSIDE the retained window asks for segments
    // that do not exist on the server. Clamping would dump the viewer
    // somewhere they didn't ask for; instead the transcode restarts AT the
    // target (the server re-spawns ffmpeg with -ss <target> — far skips work
    // without keeping the whole movie on disk). Debounced so a drag across the
    // bar collapses to one restart at the release position; any in-window
    // seek cancels a pending restart.
    if (this.source?.transcoded === true && this.seekBounds) {
      const low = this.seekBounds.start + this.transcodedOffset;
      const high = this.seekBounds.end + this.transcodedOffset;
      if (target < low || target > high) {
        this.debounceTranscodedSeek(target);
        return;
      }
    }
    this.cancelTranscodedSeek();
    const targetTime = this.clampSeek(target);
    this.lastSeekTarget = targetTime;
    // Apply immediately when the element has any timeline (finite duration, or
    // a live/MSE timeline where currentTime writes are legal); defer to
    // loadedmetadata when nothing is attached yet (resume from Continue
    // Watching / detail overlay seeks immediately after play() resolves).
    if (this.video.readyState >= 1 && !Number.isNaN(this.video.duration)) {
      this.pendingSeek = null;
      this.video.currentTime = this.toElementTime(targetTime);
    } else {
      this.pendingSeek = targetTime;
    }
  }

  /**
   * Arm (or refresh) a pending far-seek restart. The newest out-of-window
   * target wins; the restart fires when seeks settle for the debounce window.
   */
  private debounceTranscodedSeek(target: number): void {
    this.transcodedSeekTarget = target;
    if (this.transcodedSeekTimer) return; // already armed — latest value wins
    this.transcodedSeekTimer = setTimeout(() => {
      this.transcodedSeekTimer = null;
      const settled = this.transcodedSeekTarget;
      this.transcodedSeekTarget = null;
      if (settled === null) return;
      void this.restartTranscodedAt(settled);
    }, this.transcodedSeekDebounceMs);
    this.timers.add(this.transcodedSeekTimer);
  }

  /** Cancel a pending far-seek restart (the user sought back into the window). */
  private cancelTranscodedSeek(): void {
    if (!this.transcodedSeekTimer) return;
    clearTimeout(this.transcodedSeekTimer);
    this.timers.delete(this.transcodedSeekTimer);
    this.transcodedSeekTimer = null;
    this.transcodedSeekTarget = null;
  }

  /**
   * Re-spawn the transcode at a new movie-time offset. The manifest URL gains
   * `?seek=<seconds>`; the server kills the old session and starts a fresh
   * ffmpeg at that position (services/api hls-transcode). The new session is
   * 0-based again, so the recorded offset maps movie time = element time +
   * offset everywhere it crosses the boundary.
   */
  private async restartTranscodedAt(target: number): Promise<void> {
    const url = this.sources[this.sourceIndex];
    if (!url) return;
    // Replace any existing seek param rather than appending: the URL stays in
    // the playlist source, so a second far seek must not stack ?seek=&seek=.
    const [base, query = ""] = url.split("?", 2);
    const params = new URLSearchParams(query);
    params.set("seek", String(Math.round(target)));
    const seekUrl = `${base}?${params.toString()}`;
    this.transcodedOffset = Math.round(target);
    // Stale bounds belong to the old session — a seek landing during the
    // reload must not be judged against them.
    this.seekBounds = null;
    this.sources[this.sourceIndex] = seekUrl;
    const resume = this.state.status === "playing";
    // A fresh session starts fresh: the budget spent on the movie up to now
    // belongs to the session that was just killed, and none of it may count
    // against the restart (it is a different set of segments).
    this.beginFirstFrameBudget();
    try {
      await this.loadCurrentSource();
    } catch (error) {
      this.fail(error);
      return;
    }
    // hls.js starts the new playlist at its beginning (session time 0 = movie
    // time `target`); lastSeekTarget anchors it exactly once metadata lands.
    this.lastSeekTarget = target;
    if (resume) void this.play();
  }

  setVolume(volume: number): void {
    this.ensureAlive();
    const next = Math.max(0, Math.min(1, volume));
    this.video.volume = next;
    this.setState({ volume: next, muted: this.video.muted });
  }

  setMuted(muted: boolean): void {
    this.ensureAlive();
    // The element's `muted` flag — NOT volume. Setting volume to 0 would
    // destroy the user's last level, so unmute restores exactly what they had.
    this.video.muted = muted;
    this.setState({ muted, volume: this.video.volume });
  }

  updateDuration(seconds: number): void {
    this.ensureAlive();
    // The seek bar and progress saving read `durationSeconds` from the
    // CURRENT source; a transcoded pipe cannot report its own length, so an
    // async caller (TMDB runtime after a Search/Home click) patches it here.
    // Mutate the source and emit statechange so the store syncs the new
    // duration without touching playback.
    if (!this.source) return;
    this.source = { ...this.source, durationSeconds: seconds };
    this.setState({ source: this.source });
  }

  /**
   * The stream's audio and subtitle tracks. hls.js enumerates both from the
   * master playlist; for the other engines (mpegts.js, plain mp4) only the
   * element's own text tracks exist, which is where an mp4's embedded
   * subtitles show up.
   */
  async tracks(): Promise<MediaTrack[]> {
    const found: MediaTrack[] = [];
    const hls = this.hls;
    if (hls) {
      hls.audioTracks.forEach((track, index) => {
        found.push({
          id: `a${index}`,
          kind: "audio",
          label: track.name || track.lang || `Audio ${index + 1}`,
          ...(track.lang ? { language: track.lang } : {}),
          selected: hls.audioTrack === index,
        });
      });
      hls.subtitleTracks.forEach((track, index) => {
        found.push({
          id: `s${index}`,
          kind: "text",
          label: track.name || track.lang || `Subtitles ${index + 1}`,
          ...(track.lang ? { language: track.lang } : {}),
          selected: hls.subtitleTrack === index,
        });
      });
      return found;
    }
    const textTracks = this.video.textTracks;
    for (let index = 0; index < textTracks.length; index += 1) {
      const track = textTracks[index];
      if (!track || (track.kind !== "subtitles" && track.kind !== "captions")) continue;
      found.push({
        id: `t${index}`,
        kind: "text",
        label: track.label || track.language || `Subtitles ${index + 1}`,
        ...(track.language ? { language: track.language } : {}),
        selected: track.mode === "showing",
      });
    }
    return found;
  }

  selectTrack(kind: "audio" | "text", id: string | null): void {
    const hls = this.hls;
    const index = id ? Number.parseInt(id.slice(1), 10) : -1;
    if (hls) {
      if (kind === "audio") {
        if (index >= 0) hls.audioTrack = index;
      } else {
        // -1 is hls.js's "no subtitles".
        hls.subtitleDisplay = index >= 0;
        hls.subtitleTrack = index;
      }
      return;
    }
    if (kind !== "text") return;
    const textTracks = this.video.textTracks;
    for (let i = 0; i < textTracks.length; i += 1) {
      const track = textTracks[i];
      if (track) track.mode = i === index ? "showing" : "disabled";
    }
  }

  setPlaybackRate(rate: number): void {
    this.ensureAlive();
    this.video.playbackRate = Math.max(0.25, Math.min(4, rate));
  }

  /** The picture is the element here, so its box fitting IS the aspect mode. */
  setAspectMode(mode: AspectMode): void {
    this.video.style.objectFit = mode === "fill" ? "cover" : mode === "stretch" ? "fill" : "contain";
    this.video.style.transform = mode === "zoom" ? `scale(${ZOOM_FACTOR})` : "";
  }

  getState(): PlayerState {
    return { ...this.state, source: this.state.source ? { ...this.state.source } : undefined };
  }

  on(event: PlayerEvent, callback: (payload: PlayerEventPayload) => void): () => void {
    return this.emitter.on(event, callback);
  }

  /**
   * Stop the current source and release its upstream connection, while keeping
   * the player usable for the next load.
   *
   * `pause()` is NOT sufficient to close a stream: it halts playback but leaves
   * the engine attached and the HTTP request open, so the server's ffmpeg child
   * keeps running and keeps holding a provider session. Any pending reconnect
   * also stays armed, which is how a closed video reopens itself later.
   *
   * Bumping the generation invalidates in-flight retries and reconnects (they
   * all check `generation !== this.loadGeneration`), and destroying the engine
   * closes the socket so the server sees the disconnect and reaps ffmpeg.
   */
  stop(): void {
    if (this.destroyed) return;
    this.loadGeneration += 1;
    this.retryInFlight = false;
    this.mpegtsNetworkRetries = 0;
    this.liveReconnects = 0;
    this.stallRecovers = 0;
    this.pendingSeek = null;
    this.lastSeekTarget = null;
    this.transcodedOffset = 0;
    this.transcodedSeekTarget = null;
    this.clearTimers();
    this.destroyHls();
    this.destroyMpegts();
    this.video.removeAttribute("src");
    this.video.load();
    this.source = undefined;
    this.sources = [];
    this.setState({ status: "idle", source: undefined, currentTime: 0, duration: 0, bufferedAhead: 0, error: undefined });
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.clearTimers();
    this.destroyHls();
    this.destroyMpegts();
    this.video.removeEventListener("playing", this.onPlaying);
    this.video.removeEventListener("pause", this.onPause);
    this.video.removeEventListener("waiting", this.onWaiting);
    this.video.removeEventListener("ended", this.onEnded);
    this.video.removeEventListener("timeupdate", this.onTimeUpdate);
    this.video.removeEventListener("loadedmetadata", this.onLoadedMetadata);
    this.video.removeEventListener("error", this.onVideoError);
    this.video.removeAttribute("src");
    this.video.load();
    this.emitter.clear();
  }

  private async loadCurrentSource(): Promise<void> {
    const generation = this.loadGeneration;
    const url = this.sources[this.sourceIndex];
    if (!url) {
      const error = new PlayerFailure("unavailable", "All available stream URLs failed.", false, this.source?.url);
      this.fail(error);
      throw error;
    }
    // A transcoded VOD arrives as a mini-HLS playlist (finite fMP4 segments —
    // docs/stream-telemetry.md: an unbounded pipe is throttled ~5x over h2,
    // finite segment responses are not), so it MUST take the HLS engine no
    // matter what the original .mkv/.avi filename suggests.
    const mode = this.source?.transcoded === true ? "hls" : (this.source?.format ?? detectPlaybackMode(url));
    this.setState({
      status: this.sourceIndex === 0 ? "loading" : "retrying",
      attempt: this.sourceIndex + 1,
      mode,
      error: undefined,
    });
    // Armed per attempt, so a mirror and a live reconnect each get the same fair
    // chance the first URL got. Worst case is one timeout per attempt (mirrors,
    // then MAX_LIVE_RECONNECTS): the chain's own delays are seconds, so a channel
    // that never produces a frame reaches the error card in a couple of minutes
    // rather than never. In practice the fetcher timeouts escalate long before
    // this deadline is reached twice.
    this.armFirstFrameTimer(generation);

    if (mode === "hls") {
      if (this.isHlsSupported()) {
        await this.loadWithHls(url, generation);
        return;
      }
      if (this.video.canPlayType("application/vnd.apple.mpegurl")) {
        this.loadNative(url);
        return;
      }
      throw new PlayerFailure("unsupported", "This browser cannot play HLS streams.", false, url);
    }
    if (mode === "mpegts") {
      if (this.isMpegtsSupported()) {
        await this.loadWithMpegts(url, generation);
        return;
      }
      throw new PlayerFailure("unsupported", "This browser cannot play MPEG-TS streams.", false, url);
    }
    // native direct sources (mp4/webm): hand the URL to the media element.
    this.loadNative(url);
  }

  private async loadWithHls(url: string, generation: number): Promise<void> {
    this.destroyHls();
    this.destroyMpegts();
    this.nativeSourceUrl = undefined;
    // Provider MPEG-TS carries ADTS audio whose header can mislabel HE-AAC as
    // "AAC Main" (objectType 1), which Chromium refuses to put in a SourceBuffer
    // — a fatal, unrecoverable error for an otherwise decodable stream. See
    // mse-codec-fallback.ts; this must be in place before hls.js creates buffers.
    installMseAudioCodecFallback();
    // A growing EVENT playlist is "live" to hls.js, which would start at the
    // live edge — the far end of what has been transcoded so far. A movie must
    // start at zero, and hls.js honors config.startPosition for live playlists
    // ("config" start when mainStart >= playlistStart), so the position is
    // anchored by the config alone. NOTE: do NOT also listen for LEVEL_LOADED
    // to snap currentTime back to 0 — that event re-fires on every playlist
    // refresh, and once playback has advanced past ~0.5s the snap restarts the
    // movie 2-3s in (observed 2026-08-26: plays, restarts once, then smooth).
    const transcoded = this.source?.transcoded === true;
    const hls = this.hlsFactory(
      transcoded
        ? {
            ...this.config,
            startPosition: 0,
            // The transcode produces far faster than realtime, so the EVENT
            // playlist's live edge races ahead of the viewer. Without this,
            // hls.js's latency-chasing would JUMP playback forward every time
            // the viewer fell more than liveMaxLatencyDurationCount segments
            // behind an edge that means nothing for a movie.
            liveMaxLatencyDurationCount: Infinity,
          }
        : this.config,
    );
    this.hls = hls;
    hls.on(Events.ERROR, (_event, data) => {
      if (this.destroyed || generation !== this.loadGeneration || hls !== this.hls || !data.fatal) return;
      const code = data.type === ErrorTypes.NETWORK_ERROR ? "network" : "manifest";
      void this.tryNextSource(new PlayerFailure(code, "The stream failed while loading.", true, url), generation);
    });
    hls.on(Events.LEVEL_SWITCHED, (_event, data) => {
      this.emitter.emit("levelSwitched", { level: data.level });
    });
    // Keep the seek clamp in sync with the sliding window: LEVEL_LOADED fires
    // on every playlist refresh, so fragments[0].start (oldest retained
    // segment) and the last fragment's end (the transcode frontier) always
    // reflect what is actually on disk.
    hls.on(Events.LEVEL_LOADED, (_event, data) => {
      const details = (data as { details?: { fragments?: Array<{ start: number; end: number }> } }).details;
      const fragments = details?.fragments;
      if (!fragments || fragments.length === 0) return;
      this.seekBounds = {
        start: fragments[0]?.start ?? 0,
        end: fragments[fragments.length - 1]?.end ?? 0,
      };
    });
    hls.attachMedia(this.video);
    hls.loadSource(url);
  }

  private async loadWithMpegts(url: string, generation: number): Promise<void> {
    this.destroyHls();
    this.destroyMpegts();
    this.nativeSourceUrl = undefined;
    // Same ADTS mislabeling as the HLS path: mpegts.js derives its audio codec
    // from the same header bits, so the repair has to be installed here too.
    installMseAudioCodecFallback();
    const factory = this.mpegtsFactory ?? (await getMpegtsFactory());
    // Direct VOD and live channels are finite/live-shaped but served as
    // continuous feeds by the proxy, so both stay isLive: false (deep buffer,
    // no edge chasing). A TRANSCODED VOD is a live-shaped pipe with no
    // content-length or ranges — it must be isLive: true or the VOD loader
    // stalls and reopens, exhausting the provider connection gate. mpegts.js
    // only flips to live mode when the source carries isLive: true, so the
    // flag is passed there (and mirrored in the config) just for transcode
    // pipes; regular streams keep the source flag unset.
    const transcoded = this.source?.transcoded === true;
    const player = factory.createPlayer(
      {
        type: "mpegts",
        url,
        // A transcode pipe is NEVER seekable, whatever its duration is. It has
        // no Content-Length and no range support, so the finite VOD loader
        // cannot work against it: supplying a `filesize` estimate only moves
        // WHERE the loader wrongly concludes EOF, after which it abandons the
        // stream and reopens — and every reopen spawns a fresh ffmpeg holding
        // its own provider connection.
        //
        // Measured on `180 - 2026` (2026-08-26): ERR_ABORTED followed by a
        // reopen, effective delivery 1.85 Mbps against a ~7 Mbps path, while
        // the provider fed the VPS at 42 Mbps and ffmpeg sat at 8% CPU with
        // `-c:v copy`. Nothing upstream was slow. Live TV traverses the
        // identical proxy, path and VPS on the live loader and plays fine,
        // which isolates the loader — not bandwidth — as the cause.
        //
        // Duration is still available to the UI through
        // `PlayerSource.durationSeconds`, which is what drives the seek bar.
        // Knowing how long something is does not make it seekable; conflating
        // those two is what this line previously did.
        //
        // Set only for transcode pipes: for every other stream `isLive` is
        // managed by the config alone and must stay absent from the source.
        ...(transcoded ? { isLive: true } : {}),
      },
      transcoded ? transcodedMpegtsConfig : this.mpegtsConfig,
    );
    this.mpegts = player;
    player.on(factory.Events.ERROR, (...args: unknown[]) => {
      if (this.destroyed || generation !== this.loadGeneration || player !== this.mpegts) return;
      const details = typeof args[1] === "string" ? args[1] : "";
      const isCodecOrFormat =
        details === factory.ErrorDetails.MEDIA_CODEC_UNSUPPORTED || details === factory.ErrorDetails.MEDIA_FORMAT_ERROR;
      if (!isCodecOrFormat && this.mpegtsNetworkRetries < MAX_MPEGTS_NETWORK_RETRIES) {
        this.mpegtsNetworkRetries += 1;
        this.setState({ status: "retrying", attempt: this.mpegtsNetworkRetries, mode: "mpegts", error: undefined });
        this.emitter.emit("retrying", this.getState());
        void this.reloadMpegts(url, generation);
        return;
      }
      const code: PlayerErrorCode = isCodecOrFormat ? "media" : "network";
      const message = isCodecOrFormat
        ? "The stream uses a codec this browser cannot decode."
        : "The stream failed while loading.";
      void this.tryNextSource(new PlayerFailure(code, message, true, url), generation);
    });
    player.attachMediaElement(this.video);
    player.load();
  }

  private loadNative(url: string): void {
    this.destroyHls();
    this.destroyMpegts();
    this.nativeSourceUrl = url;
    this.video.src = url;
    this.video.load();
  }

  private async reloadMpegts(url: string, generation: number): Promise<void> {
    if (this.destroyed || generation !== this.loadGeneration) return;
    const delay = this.networkRetryBackoffMs(this.mpegtsNetworkRetries);
    await this.after(delay, generation);
    if (this.destroyed || generation !== this.loadGeneration) return;
    await this.loadWithMpegts(url, generation);
  }

  /** Resolve after `ms` unless a newer load/destroy happened meanwhile. */
  private after(ms: number, generation: number): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.timers.delete(timer);
        if (this.destroyed || generation !== this.loadGeneration) return;
        resolve();
      }, ms);
      this.timers.add(timer);
    });
  }

  private clearTimers(): void {
    for (const timer of this.timers) clearTimeout(timer);
    this.timers.clear();
    this.stallTimer = null;
    this.firstFrameTimer = null;
  }

  /** Start the clock for one start, from its first attempt to its last. */
  private beginFirstFrameBudget(): void {
    this.firstFrameBudgetEndsAt = Date.now() + this.firstFrameBudgetMs;
  }

  /**
   * A deadline for a source to show its first frame.
   *
   * Armed for every attempt (initial URL, each mirror, each live reconnect) and
   * disarmed the moment a frame plays. On expiry it escalates through the same
   * chain a fatal engine error uses, so a channel that never starts ends in the
   * viewer-facing reason card — "did not start in time" — instead of a loading
   * state that outlives the viewer's patience.
   *
   * Every attempt shares one budget (FIRST_FRAME_BUDGET_MS): an attempt whose
   * share would run past it is shortened to what is left, so the chain can never
   * stack its way to minutes of "Retrying…".
   */
  private armFirstFrameTimer(generation: number): void {
    this.clearFirstFrameTimer();
    const url = this.sources[this.sourceIndex];
    const perAttempt =
      this.firstFrameAttempts === 0
        ? this.firstFrameTimeoutMs
        : Math.min(this.firstFrameTimeoutMs, FIRST_FRAME_RETRY_GRACE_MS);
    this.firstFrameAttempts += 1;
    const remaining = this.firstFrameBudgetEndsAt - Date.now();
    const budgetCapped = remaining < perAttempt;
    const allowance = Math.max(0, Math.min(perAttempt, remaining));
    // The card reports the wait for the START, not for the final attempt: when
    // the budget is what ended it, the attempt's own short share is not the
    // number the viewer lived through.
    const waitedSeconds = Math.round((budgetCapped ? this.firstFrameBudgetMs : allowance) / 1000);
    this.firstFrameTimer = setTimeout(() => {
      this.firstFrameTimer = null;
      if (this.destroyed || generation !== this.loadGeneration) return;
      // Frames have been through this element already: this is a STALL, and the
      // stall watchdog owns that case (it reloads and escalates on its own).
      if (this.state.status === "playing" || this.video.currentTime > 0) return;
      void this.tryNextSource(
        new PlayerFailure("timeout", "The stream did not start in time.", true, url),
        generation,
        `The stream did not start in time (${waitedSeconds}s).`,
        "timeout",
      );
    }, allowance);
    this.timers.add(this.firstFrameTimer);
  }

  private clearFirstFrameTimer(): void {
    if (!this.firstFrameTimer) return;
    clearTimeout(this.firstFrameTimer);
    this.timers.delete(this.firstFrameTimer);
    this.firstFrameTimer = null;
  }

  private async tryNextSource(
    failure: PlayerFailure,
    generation = this.loadGeneration,
    /** Reported when the whole chain ends in an error; the last attempt's own
     *  reason is more use to the viewer than the generic recovery sentence. */
    finalMessage?: string,
    /** The code the chain ends on; `unavailable` unless a caller knows better
     *  (the first-frame deadline ends as `timeout`). Never `exhausted`: that is
     *  the free-tier meter's code. */
    finalCode: PlayerErrorCode = "unavailable",
  ): Promise<void> {
    if (this.destroyed || generation !== this.loadGeneration || this.retryInFlight) return;
    // A recovery chain supersedes any pending watchdog — neither the stall timer
    // nor the first-frame deadline may fire mid-reload and step on the hls
    // instance being (re)created. loadCurrentSource arms a fresh deadline for
    // whichever attempt it starts.
    this.clearStallTimer();
    this.clearFirstFrameTimer();
    this.retryInFlight = true;
    if (this.sourceIndex + 1 < this.sources.length) {
      this.sourceIndex += 1;
      this.mpegtsNetworkRetries = 0;
      this.setState({ status: "retrying", attempt: this.sourceIndex + 1 });
      this.emitter.emit("retrying", this.getState());
      try {
        await this.loadCurrentSource();
      } catch (error) {
        this.retryInFlight = false;
        await this.tryNextSource(error instanceof PlayerFailure ? error : failure, generation, finalMessage, finalCode);
      }
      this.retryInFlight = false;
      return;
    }

    // Every URL failed. Live channels recover the way SparkleTV/ExoPlayer do:
    // after a short pause, reload from the top — the provider's auth layer
    // (407/502 bursts) often settles. Transcoded VOD is a live-shaped pipe,
    // so it recovers the same way. Codec/media errors never auto-reconnect
    // because retrying cannot fix an undecodable stream.
    // Reconnect-on-stall applies to LIVE ONLY — deliberately not to transcoded
    // VOD, even though a transcode is served as a live-shaped pipe.
    //
    // Rejoining the edge is the right recovery for a live channel: the content
    // is "now", so restarting costs the viewer nothing. A MOVIE is the opposite.
    // Reconnecting restarts it from 00:00, throwing away the viewer's position,
    // and every reconnect mints a fresh provider session while the previous one
    // is still held — which pins the account at its connection cap and starves
    // every stream, including the one that just restarted. That makes the next
    // stall more likely, so the loop reinforces itself.
    //
    // Observed 2026-08-26 on `180 - 2026`: playback ran cleanly to 44s, hit one
    // brief stall, and reset to 0.0 — matching the ~30-60s session spacing in the
    // provider panel, where five sessions for one movie sat open simultaneously.
    //
    // A transcoded VOD that stalls should recover by buffering, not by restarting.
    const isLive = this.source?.type === "live";
    const isDecodeFailure = failure.code === "media";
    if (isLive && !isDecodeFailure && this.liveReconnects < this.maxLiveReconnects) {
      this.liveReconnects += 1;
      this.sourceIndex = 0;
      this.mpegtsNetworkRetries = 0;
      this.setState({ status: "retrying", attempt: this.sources.length + this.liveReconnects, error: undefined });
      this.emitter.emit("retrying", this.getState());
      this.retryInFlight = false;
      try {
        await this.after(this.liveReconnectDelayMs, generation);
        if (this.destroyed || generation !== this.loadGeneration) return;
        await this.loadCurrentSource();
      } catch (error) {
        this.retryInFlight = false;          await this.tryNextSource(error instanceof PlayerFailure ? error : failure, generation, finalMessage, finalCode);
      }
      return;
    }

    this.retryInFlight = false;
    this.fail(
      new PlayerFailure(
        finalCode,
        finalMessage ?? "The stream could not be recovered after trying all URLs.",
        false,
        failure.sourceUrl,
      ),
    );
  }

  private fail(error: unknown): void {
    const playerError = toPlayerError(error, this.sources[this.sourceIndex]);
    this.setState({ status: "error", error: playerError });
    this.emitter.emit("error", playerError);
  }

  private setState(patch: Partial<PlayerState>): void {
    this.state = { ...this.state, ...patch };
    const snapshot = this.getState();
    this.emitter.emit("statechange", snapshot);
  }

  private onPlaying = (): void => {
    this.clearStallTimer();
    // A frame is on screen: the source started, so the start deadline is spent.
    this.clearFirstFrameTimer();
    // Reset the recovery budget only after sustained clean playback, so a
    // channel that blips every few seconds escalates instead of reloading
    // forever (see STALL_RESET_GRACE_MS).
    if (Date.now() - this.lastRecoveryAt >= STALL_RESET_GRACE_MS) {
      this.stallRecovers = 0;
    }
    this.setState({ status: "playing", error: undefined });
    this.emitter.emit("playing", this.getState());
  };

  private onPause = (): void => {
    if (this.video.ended) return;
    // A failed source stays failed until the next load: the element's own
    // events after the failure must not hide the error (see onWaiting).
    if (this.state.status === "error") return;
    // A finite VOD response can briefly emit `pause` while the MSE engine is
    // still attaching its first media buffer. Do not treat that transport
    // pause as a user pause; mpegts.js will emit `playing` when data resumes.
    if (this.source?.type === "vod" && (this.state.status === "loading" || this.state.status === "buffering" || this.state.status === "retrying")) return;
    this.setState({ status: "paused" });
    this.emitter.emit("paused", this.getState());
  };

  private onWaiting = (): void => {
    // Only arm the stall watchdog for a source that was already playing and
    // then lost data — a stall during the initial load is handled by the
    // network-retry / fatal-error path, not the watchdog.
    // A failed source stays failed until the next load. The element still says
    // "waiting" after the engine gave up, and that turned the error back into
    // "buffering": a film the server could not start sat on a black screen at
    // 0:00 with no message and no Retry (2026-10-07).
    if (this.state.status === "error") return;
    const wasPlaying = this.state.status === "playing";
    this.setState({ status: "buffering" });
    this.emitter.emit("buffering", this.getState());
    if (wasPlaying) this.armStallTimer();
  };

  private armStallTimer(): void {
    // LIVE ONLY. The watchdog reloads the source from scratch, which for a live
    // channel is harmless — the content is "now", so rejoining the edge costs the
    // viewer nothing. For a MOVIE it restarts playback from 00:00 and mints a
    // fresh provider session, and because the reload begins with an empty buffer
    // it is likely to stall again immediately: the watchdog re-arms and the movie
    // never gets past the first few seconds.
    //
    // Measured 2026-08-26: playback reached 6s, stalled, and the trace showed
    // longestStall 8.6s — the 8s timeout firing — followed by a reset to 0.0.
    // Meanwhile the stream endpoint was sustaining 12.31 Mbps, so there was
    // nothing wrong with the stream the reload was "recovering" from.
    //
    // A transcoded VOD that stalls must wait for its buffer, not restart. This is
    // the same distinction as the live-reconnect path in tryNextSource().
    if (this.stallTimer || this.source?.type !== "live") return;
    this.stallTimer = setTimeout(() => {
      this.stallTimer = null;
      void this.recoverFromStall();
    }, this.stallTimeoutMs);
    this.timers.add(this.stallTimer);
  }

  private clearStallTimer(): void {
    if (!this.stallTimer) return;
    clearTimeout(this.stallTimer);
    this.timers.delete(this.stallTimer);
    this.stallTimer = null;
  }

  private async recoverFromStall(): Promise<void> {
    const generation = this.loadGeneration;
    if (this.destroyed || generation !== this.loadGeneration) return;
    const url = this.sources[0];
    if (!url) return;
    if (this.stallRecovers >= this.maxStallRecovers) {
      // Repeated reloads didn't fix it — escalate to the normal live
      // reconnect / exhausted-error path so a dead channel surfaces an
      // error instead of silently looping forever.
      this.stallRecovers = 0;
      void this.tryNextSource(new PlayerFailure("network", "Playback stalled repeatedly.", true, url), generation);
      return;
    }
    this.stallRecovers += 1;
    // Restart from the top URL, matching the live-reconnect path: a stale
    // feed may be the one that died, and the primary is the best candidate.
    this.sourceIndex = 0;
    this.mpegtsNetworkRetries = 0;
    this.lastRecoveryAt = Date.now();
    this.setState({ status: "retrying", attempt: this.stallRecovers, error: undefined });
    this.emitter.emit("retrying", this.getState());
    try {
      await this.loadCurrentSource();
    } catch (error) {
      this.fail(error);
    }
  }

  private onEnded = (): void => {
    this.setState({ status: "ended" });
    this.emitter.emit("ended", this.getState());
  };

  private lastProgressTime = -1;

  private onTimeUpdate = (): void => {
    const elementTime = this.video.currentTime;
    // Only disarm the watchdog when currentTime actually advanced — browsers
    // can fire a final timeupdate as playback stalls, which would otherwise
    // clear the just-armed timer and leave the freeze unwatched.
    if (elementTime > this.lastProgressTime && (this.state.status === "buffering" || this.state.status === "playing")) {
      this.clearStallTimer();
    }
    this.lastProgressTime = elementTime;
    const buffered = this.video.buffered;
    let bufferedAhead = 0;
    for (let index = 0; index < buffered.length; index += 1) {
      if (buffered.start(index) <= elementTime && elementTime <= buffered.end(index)) {
        bufferedAhead = Math.max(0, buffered.end(index) - elementTime);
        break;
      }
    }
    // Emit MOVIE time (element time + session offset) so the seek bar and
    // Continue Watching stay on the film's timeline even after a far-seek
    // restart re-anchored the element at 0.
    const currentTime = this.toMovieTime(elementTime);
    this.setState({ currentTime, duration: this.video.duration || 0, bufferedAhead });
    this.emitter.emit("timeupdate", { currentTime, duration: this.video.duration || 0, bufferedAhead });
  };

  private onLoadedMetadata = (): void => {
    this.setState({ duration: this.video.duration || 0 });
    // Any element that reached metadata has a timeline currentTime can be
    // written to — including the live-duration case (transcoded VOD reports
    // Infinity), where the old finite-duration guard silently dropped the
    // pending seek. clampSeek keeps the write inside what the server retains.
    if (this.video.readyState >= 1) {
      const target = this.pendingSeek ?? this.lastSeekTarget;
      if (target !== null) {
        this.pendingSeek = null;
        // A pending seek requested BEFORE the manifest loaded (Continue
        // Watching resume) may land outside the now-known window — restart
        // the transcode at it instead of clamping to the frontier.
        if (this.source?.transcoded === true && this.seekBounds) {
          const low = this.seekBounds.start + this.transcodedOffset;
          const high = this.seekBounds.end + this.transcodedOffset;
          if (target < low || target > high) {
            this.lastSeekTarget = target;
            this.debounceTranscodedSeek(target);
            return;
          }
        }
        this.video.currentTime = this.toElementTime(this.clampSeek(target));
        this.setState({ currentTime: this.toMovieTime(this.video.currentTime) });
      }
    }
    this.emitter.emit("loadedmetadata", this.getState());
  };

  private onVideoError = (): void => {
    if (this.destroyed || this.hls || this.mpegts || this.nativeSourceUrl !== this.sources[this.sourceIndex]) return;
    void this.tryNextSource(new PlayerFailure("media", "The browser could not decode this stream.", true, this.sources[this.sourceIndex]));
  };

  private destroyHls(): void {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
  }

  private destroyMpegts(): void {
    if (this.mpegts) {
      this.mpegts.destroy();
      this.mpegts = null;
    }
  }

  private ensureAlive(): void {
    if (this.destroyed) throw new Error("Player has been destroyed");
  }
}

export { defaultHlsConfig };
